import { useCallback, useEffect, useState } from "react";
import type { MgbaCore } from "../emulator/types";
import { detectGuide } from "./detection";
import { loadGuideSections, type GuideSection } from "./loader";
import {
  loadProgress,
  saveProgress,
  setCurrentSection,
  setScrollOffset,
  updateCheckbox,
  type GuideProgress,
} from "./progress";

export interface GuideState {
  available: boolean;
  guidePath: string | null;
  gameCode: string | null;
  sections: GuideSection[];
  progress: GuideProgress;
  isLoading: boolean;
  error: string | null;
}

export function useGuide(
  core: MgbaCore | null,
  currentRomName: string | null,
): GuideState & {
  setCurrentSection(sectionId: string | null): void;
  updateCheckbox(checkboxIndex: number, checked: boolean): void;
  setScrollOffset(offset: number): void;
} {
  const [state, setState] = useState<GuideState>({
    available: false,
    guidePath: null,
    gameCode: null,
    sections: [],
    progress: { currentSectionId: null, scrollOffset: 0, checkboxState: {} },
    isLoading: false,
    error: null,
  });

  const romBaseName = currentRomName?.replace(/\.[^.]+$/, "") || "";

  // Load guide info when ROM changes
  useEffect(() => {
    if (!core || !currentRomName) {
      setState((prev) => ({
        ...prev,
        available: false,
        guidePath: null,
        gameCode: null,
        sections: [],
      }));
      return;
    }

    let cancelled = false;

    (async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const guideInfo = await detectGuide(core, currentRomName);
        if (cancelled) return;

        if (!guideInfo) {
          setState((prev) => ({
            ...prev,
            available: false,
            isLoading: false,
          }));
          return;
        }

        // Load guide sections
        const sections = await loadGuideSections(guideInfo.guidePath);
        if (cancelled) return;

        // Open on the first page unless the saved one still exists.
        const saved = loadProgress(romBaseName);
        const progress = sections.some((s) => s.id === saved.currentSectionId)
          ? saved
          : { ...saved, currentSectionId: sections[0]?.id ?? null, scrollOffset: 0 };

        setState((prev) => ({
          ...prev,
          available: true,
          guidePath: guideInfo.guidePath,
          gameCode: guideInfo.gameCode,
          sections,
          progress,
          isLoading: false,
        }));
      } catch (e) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: e instanceof Error ? e.message : "Failed to load guide",
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [core, currentRomName, romBaseName]);

  // Save progress when it changes
  useEffect(() => {
    if (state.available && romBaseName) {
      saveProgress(romBaseName, state.progress);
    }
  }, [state.progress, state.available, romBaseName]);

  const handleSetCurrentSection = useCallback((sectionId: string | null) => {
    setState((prev) => ({
      ...prev,
      progress: setCurrentSection(prev.progress, sectionId),
    }));
  }, []);

  const handleUpdateCheckbox = useCallback((checkboxIndex: number, checked: boolean) => {
    setState((prev) => ({
      ...prev,
      progress: updateCheckbox(prev.progress, checkboxIndex, checked),
    }));
  }, []);

  const handleSetScrollOffset = useCallback((offset: number) => {
    setState((prev) => ({
      ...prev,
      progress: setScrollOffset(prev.progress, offset),
    }));
  }, []);

  return {
    ...state,
    setCurrentSection: handleSetCurrentSection,
    updateCheckbox: handleUpdateCheckbox,
    setScrollOffset: handleSetScrollOffset,
  };
}
